import { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'
import { forEach } from 'lodash'
import { Audio, AudioListener, AudioLoader } from 'three'

import { usePageVisibility } from '@/hooks'
import { useLayoutStore } from '@/store/layout'
import { useLoader } from '@/store/loader'
import { isLgScreen } from '@/utils/screen'
import { BASE_PATH } from '@/constants'

import MixerController from './MixerController'
import { useMixerStore } from './useMixerStore'

export const MixerContext = createContext<
  // eslint-disable-next-line no-unused-vars
  { detune: (key: string, value: number) => void; toggleSound: () => void } | undefined
>(undefined)

const sounds = [
  {
    name: 'ambient',
    src: `${BASE_PATH}/sounds/music.mp3`
  },
  {
    name: 'fx',
    src: `${BASE_PATH}/sounds/fx.mp3`
  }
]

const MixerProvider = ({ children }: PropsWithChildren) => {
  const [loader] = useState(() => new AudioLoader())
  const [playlist] = useState(() => new Map<string, Audio>())
  const setLayoutState = useLayoutStore((state) => state.setState)
  const documentHidden = usePageVisibility()

  const setup = useCallback(() => {
    const listener = new AudioListener()
    forEach(sounds, (sound) => {
      loader.load(sound.src as string, (buffer) => {
        const track = new Audio(listener)
        const key = sound.name
        const loop = key === 'ambient'
        const volume = 1
        track.setVolume(volume)
        track.setBuffer(buffer)
        track.setLoop(loop)
        track.userData = { volume }
        playlist.set(key, track)
      })
    })
  }, [loader, playlist])

  const mute = useCallback(
    (immediate = false) => {
      playlist.forEach((track) => {
        if (!immediate) {
          gsap.to(track.userData, {
            volume: 0,
            duration: 0.8,
            ease: 'none',
            onUpdate: () => {
              track.setVolume(track.userData.volume)
            }
          })
        } else {
          track.userData.volume = 0
          track.setVolume(0)
        }
      })
    },
    [playlist]
  )

  const unmute = useCallback(
    (immediate = false) => {
      playlist.forEach((track) => {
        if (!immediate) {
          gsap.to(track.userData, {
            volume: 1,
            duration: 0.8,
            ease: 'none',
            onUpdate: () => {
              track.setVolume(track.userData.volume)
            }
          })
        } else {
          track.userData.volume = 1
          track.setVolume(1)
        }
      })
    },
    [playlist]
  )

  const detune = useCallback(
    (key: string, value: number) => {
      const track = playlist.get(key)
      const progress = 1 - Math.abs(-1 + value * 2)
      const detune = -1200 * progress
      track?.setDetune(detune)
    },
    [playlist]
  )

  const playSound = useCallback(
    (key: string, restart = true) => {
      const track = playlist.get(key)
      if (track) {
        restart && track.stop()
        return track.play()
      }
    },
    [playlist]
  )

  const toggleSound = useCallback(() => {
    document.removeEventListener('click', toggleSound)
    setLayoutState({ needsAudioTrigger: false })

    const state = useMixerStore.getState()
    state.muted ? unmute() : mute()
    state.setState({ muted: !state.muted, played: true })

    if (!state.played) {
      const track = playlist.get('ambient')
      track?.play()
    }
  }, [playlist, mute, unmute, setLayoutState])

  const addListeners = useCallback(() => {
    document.addEventListener('click', toggleSound)
  }, [toggleSound])

  const removeListeners = useCallback(() => {
    document.removeEventListener('click', toggleSound)
  }, [toggleSound])

  useIsomorphicLayoutEffect(() => {
    const state = useMixerStore.getState()
    documentHidden ? mute(true) : !state.muted ? unmute(true) : null
  }, [documentHidden, mute, unmute])

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useMixerStore.subscribe(
      (state) => state.progress,
      (progress) => progress && playSound('fx')
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useMixerStore.subscribe(
      (state) => state.detune,
      (value) => detune('ambient', value)
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useLoader.subscribe(
      (state) => state.compiled,
      (compiled) => {
        if (!isLgScreen()) return
        if (compiled) {
          unsubscribe()
          addListeners()
          setup()
        }
      }
    )

    return () => {
      removeListeners()
      unsubscribe()
    }
  }, [setup])

  const contextValue = useMemo(() => ({ detune, toggleSound }), [detune, toggleSound])

  return (
    <MixerContext.Provider value={contextValue}>
      {children}
      <MixerController />
    </MixerContext.Provider>
  )
}

export default MixerProvider
